// @generated by TypeGen from LogbookApi/AccountApi.swift - DO NOT EDIT

import type { IAccountSearchResponse } from "../LogbookModel/Account"
import type { IAccountSubscriptionPlan, IAccountSubscriptionPlanFeature, IAccountSubscriptionPlanPricing } from "../LogbookModel/AccountSubscriptionPlan"
import { IAccountUserLevel } from "../LogbookModel/AccountUser"
import type { ISessionResponse } from "../LogbookModel/Session"
import type { IAccountInvitationResponse, IAccountSignupRequest, IAccountUserLevelRequest, IAccountUserUpdateLanguageRequest, IAccountUserUpdatePasswordRequest, IAccountUserUpdateRequest, IAccountUsersResponse } from "../LogbookServer/AccountApi"
import type { ISuccessResponse } from "../LogbookServer/SuccessResponse"
import { assertTarget } from "./ApiRuntime"
import type { ApiArgs, ApiClient, ApiTarget, ApiUser } from "./ApiRuntime"

export interface IAccountSearchRequest {
  query: string;
  shipId?: number;
}

export interface IAccountSubscriptionPlanInfo {
  plan: IAccountSubscriptionPlan;
  features: IAccountSubscriptionPlanFeature[];
  pricing: IAccountSubscriptionPlanPricing[];
}

export interface IAccountSubscriptionPlansResponse {
  plans: IAccountSubscriptionPlanInfo[];
  stripeKey: string;
  appAccountToken?: string;
}

export interface AccountApiEndpoints {
  "DELETE /api/account": {
    params: {};
    query: {};
    request: never;
    response: ISuccessResponse;
    targets: ApiTarget[];
    permission: { account: IAccountUserLevel };
  };
  "DELETE /api/account/users/:userId": {
    params: { userId: number };
    query: {};
    request: never;
    response: ISuccessResponse;
    targets: ApiTarget[];
    permission: { account: IAccountUserLevel };
  };
  "GET /api/account/plan": {
    params: {};
    query: {};
    request: never;
    response: IAccountSubscriptionPlanInfo;
    targets: ApiTarget[];
    permission: { account: IAccountUserLevel };
  };
  "GET /api/account/plans": {
    params: {};
    query: {};
    request: never;
    response: IAccountSubscriptionPlansResponse;
    targets: ApiTarget[];
    permission: { session: true };
  };
  "GET /api/account/users": {
    params: {};
    query: {};
    request: never;
    response: IAccountUsersResponse;
    targets: ApiTarget[];
    permission: { account: IAccountUserLevel };
  };
  "GET /api/signup/invitation/:token": {
    params: { token: string };
    query: {};
    request: never;
    response: IAccountInvitationResponse;
    targets: ApiTarget[];
    permission: {};
  };
  "POST /api/account": {
    params: {};
    query: {};
    request: IAccountSignupRequest;
    response: ISuccessResponse;
    targets: ApiTarget[];
    permission: {};
  };
  "POST /api/search": {
    params: {};
    query: {};
    request: IAccountSearchRequest;
    response: IAccountSearchResponse;
    targets: ApiTarget[];
    permission: { account: IAccountUserLevel };
  };
  "POST /api/signup/confirm-email/:token": {
    params: { token: string };
    query: {};
    request: never;
    response: ISessionResponse;
    targets: ApiTarget[];
    permission: {};
  };
  "POST /api/signup/invitation/:token": {
    params: { token: string };
    query: {};
    request: IAccountSignupRequest;
    response: ISessionResponse;
    targets: ApiTarget[];
    permission: {};
  };
  "PUT /api/account/user": {
    params: {};
    query: {};
    request: IAccountUserUpdateRequest;
    response: ISessionResponse;
    targets: ApiTarget[];
    permission: { session: true };
  };
  "PUT /api/account/user/language": {
    params: {};
    query: {};
    request: IAccountUserUpdateLanguageRequest;
    response: ISessionResponse;
    targets: ApiTarget[];
    permission: { session: true };
  };
  "PUT /api/account/user/password": {
    params: {};
    query: {};
    request: IAccountUserUpdatePasswordRequest;
    response: ISessionResponse;
    targets: ApiTarget[];
    permission: { session: true };
  };
  "PUT /api/account/users/:userId": {
    params: { userId: number };
    query: {};
    request: IAccountUserLevelRequest;
    response: ISuccessResponse;
    targets: ApiTarget[];
    permission: { account: IAccountUserLevel };
  };
}

export const accountApiEndpoints = {
  "DELETE /api/account": { targets: ["server"], permission: { account: IAccountUserLevel.admin } },
  "DELETE /api/account/users/:userId": { targets: ["server"], permission: { account: IAccountUserLevel.admin } },
  "GET /api/account/plan": { targets: ["app", "server"], permission: { account: IAccountUserLevel.user } },
  "GET /api/account/plans": { targets: ["app", "server"], permission: { session: true } },
  "GET /api/account/users": { targets: ["server"], permission: { account: IAccountUserLevel.admin } },
  "GET /api/signup/invitation/:token": { targets: ["server"], permission: {} },
  "POST /api/account": { targets: ["server"], permission: {} },
  "POST /api/search": { targets: ["app", "server"], permission: { account: IAccountUserLevel.user } },
  "POST /api/signup/confirm-email/:token": { targets: ["server"], permission: {} },
  "POST /api/signup/invitation/:token": { targets: ["server"], permission: {} },
  "PUT /api/account/user": { targets: ["server"], permission: { session: true } },
  "PUT /api/account/user/language": { targets: ["server"], permission: { session: true } },
  "PUT /api/account/user/password": { targets: ["server"], permission: { session: true } },
  "PUT /api/account/users/:userId": { targets: ["server"], permission: { account: IAccountUserLevel.admin } },
} as const;

export class AccountApi {
  constructor(private client: ApiClient) {}

  account = {
    delete: (a: ApiArgs<AccountApiEndpoints, "DELETE /api/account">) => ({
      call: (): Promise<ISuccessResponse> => {
        assertTarget(this.client, "DELETE /api/account", accountApiEndpoints["DELETE /api/account"].targets);
        return this.client.call<ISuccessResponse>("DELETE /api/account", a);
      },
      url: (): string => this.client.url("DELETE /api/account", a),
      available: (user?: ApiUser): boolean => user != null && (user.accountLevel() ?? -1) >= IAccountUserLevel.admin,
    }),
    removeUser: (a: ApiArgs<AccountApiEndpoints, "DELETE /api/account/users/:userId">) => ({
      call: (): Promise<ISuccessResponse> => {
        assertTarget(this.client, "DELETE /api/account/users/:userId", accountApiEndpoints["DELETE /api/account/users/:userId"].targets);
        return this.client.call<ISuccessResponse>("DELETE /api/account/users/:userId", a);
      },
      url: (): string => this.client.url("DELETE /api/account/users/:userId", a),
      available: (user?: ApiUser): boolean => user != null && (user.accountLevel() ?? -1) >= IAccountUserLevel.admin,
    }),
    plan: (a: ApiArgs<AccountApiEndpoints, "GET /api/account/plan">) => ({
      call: (): Promise<IAccountSubscriptionPlanInfo> => {
        assertTarget(this.client, "GET /api/account/plan", accountApiEndpoints["GET /api/account/plan"].targets);
        return this.client.call<IAccountSubscriptionPlanInfo>("GET /api/account/plan", a);
      },
      url: (): string => this.client.url("GET /api/account/plan", a),
      available: (user?: ApiUser): boolean => user != null && (user.accountLevel() ?? -1) >= IAccountUserLevel.user,
    }),
    plans: (a: ApiArgs<AccountApiEndpoints, "GET /api/account/plans">) => ({
      call: (): Promise<IAccountSubscriptionPlansResponse> => {
        assertTarget(this.client, "GET /api/account/plans", accountApiEndpoints["GET /api/account/plans"].targets);
        return this.client.call<IAccountSubscriptionPlansResponse>("GET /api/account/plans", a);
      },
      url: (): string => this.client.url("GET /api/account/plans", a),
      available: (user?: ApiUser): boolean => user != null,
    }),
    users: (a: ApiArgs<AccountApiEndpoints, "GET /api/account/users">) => ({
      call: (): Promise<IAccountUsersResponse> => {
        assertTarget(this.client, "GET /api/account/users", accountApiEndpoints["GET /api/account/users"].targets);
        return this.client.call<IAccountUsersResponse>("GET /api/account/users", a);
      },
      url: (): string => this.client.url("GET /api/account/users", a),
      available: (user?: ApiUser): boolean => user != null && (user.accountLevel() ?? -1) >= IAccountUserLevel.admin,
    }),
    create: (a: ApiArgs<AccountApiEndpoints, "POST /api/account">) => ({
      call: (): Promise<ISuccessResponse> => {
        assertTarget(this.client, "POST /api/account", accountApiEndpoints["POST /api/account"].targets);
        return this.client.call<ISuccessResponse>("POST /api/account", a);
      },
      url: (): string => this.client.url("POST /api/account", a),
      available: (_user?: ApiUser): boolean => true,
    }),
    search: (a: ApiArgs<AccountApiEndpoints, "POST /api/search">) => ({
      call: (): Promise<IAccountSearchResponse> => {
        assertTarget(this.client, "POST /api/search", accountApiEndpoints["POST /api/search"].targets);
        return this.client.call<IAccountSearchResponse>("POST /api/search", a);
      },
      url: (): string => this.client.url("POST /api/search", a),
      available: (user?: ApiUser): boolean => user != null && (user.accountLevel() ?? -1) >= IAccountUserLevel.user,
    }),
    updateUser: (a: ApiArgs<AccountApiEndpoints, "PUT /api/account/user">) => ({
      call: (): Promise<ISessionResponse> => {
        assertTarget(this.client, "PUT /api/account/user", accountApiEndpoints["PUT /api/account/user"].targets);
        return this.client.call<ISessionResponse>("PUT /api/account/user", a);
      },
      url: (): string => this.client.url("PUT /api/account/user", a),
      available: (user?: ApiUser): boolean => user != null,
    }),
    updateLanguage: (a: ApiArgs<AccountApiEndpoints, "PUT /api/account/user/language">) => ({
      call: (): Promise<ISessionResponse> => {
        assertTarget(this.client, "PUT /api/account/user/language", accountApiEndpoints["PUT /api/account/user/language"].targets);
        return this.client.call<ISessionResponse>("PUT /api/account/user/language", a);
      },
      url: (): string => this.client.url("PUT /api/account/user/language", a),
      available: (user?: ApiUser): boolean => user != null,
    }),
    updatePassword: (a: ApiArgs<AccountApiEndpoints, "PUT /api/account/user/password">) => ({
      call: (): Promise<ISessionResponse> => {
        assertTarget(this.client, "PUT /api/account/user/password", accountApiEndpoints["PUT /api/account/user/password"].targets);
        return this.client.call<ISessionResponse>("PUT /api/account/user/password", a);
      },
      url: (): string => this.client.url("PUT /api/account/user/password", a),
      available: (user?: ApiUser): boolean => user != null,
    }),
    updateUserLevel: (a: ApiArgs<AccountApiEndpoints, "PUT /api/account/users/:userId">) => ({
      call: (): Promise<ISuccessResponse> => {
        assertTarget(this.client, "PUT /api/account/users/:userId", accountApiEndpoints["PUT /api/account/users/:userId"].targets);
        return this.client.call<ISuccessResponse>("PUT /api/account/users/:userId", a);
      },
      url: (): string => this.client.url("PUT /api/account/users/:userId", a),
      available: (user?: ApiUser): boolean => user != null && (user.accountLevel() ?? -1) >= IAccountUserLevel.admin,
    }),
  };

  signup = {
    invitation: (a: ApiArgs<AccountApiEndpoints, "GET /api/signup/invitation/:token">) => ({
      call: (): Promise<IAccountInvitationResponse> => {
        assertTarget(this.client, "GET /api/signup/invitation/:token", accountApiEndpoints["GET /api/signup/invitation/:token"].targets);
        return this.client.call<IAccountInvitationResponse>("GET /api/signup/invitation/:token", a);
      },
      url: (): string => this.client.url("GET /api/signup/invitation/:token", a),
      available: (_user?: ApiUser): boolean => true,
    }),
    confirmEmail: (a: ApiArgs<AccountApiEndpoints, "POST /api/signup/confirm-email/:token">) => ({
      call: (): Promise<ISessionResponse> => {
        assertTarget(this.client, "POST /api/signup/confirm-email/:token", accountApiEndpoints["POST /api/signup/confirm-email/:token"].targets);
        return this.client.call<ISessionResponse>("POST /api/signup/confirm-email/:token", a);
      },
      url: (): string => this.client.url("POST /api/signup/confirm-email/:token", a),
      available: (_user?: ApiUser): boolean => true,
    }),
    acceptInvitation: (a: ApiArgs<AccountApiEndpoints, "POST /api/signup/invitation/:token">) => ({
      call: (): Promise<ISessionResponse> => {
        assertTarget(this.client, "POST /api/signup/invitation/:token", accountApiEndpoints["POST /api/signup/invitation/:token"].targets);
        return this.client.call<ISessionResponse>("POST /api/signup/invitation/:token", a);
      },
      url: (): string => this.client.url("POST /api/signup/invitation/:token", a),
      available: (_user?: ApiUser): boolean => true,
    }),
  };
}
